import React, { useState } from 'react';
import {
  Sparkles,
  Cpu,
  ShieldCheck,
  Activity,
  Layers,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  FileCode2,
  Lock,
  Server,
  Search,
  GitBranch,
  TableProperties,
  Award,
  TrendingUp,
  UserCheck,
  Building2,
  DollarSign,
  PieChart,
  RefreshCw,
  Sliders,
  Terminal,
  ChevronRight,
  Zap,
  Power,
  Play,
  Settings,
  ShieldAlert,
  SlidersHorizontal,
  FileCheck2,
  HelpCircle,
  Database
} from 'lucide-react';
import { AppView } from '../../types';
import {
  aiPlatformStore,
  AIProvider,
  AIModelDefinition,
  AITaskDefinition,
  AIModelRoute,
  AIPromptDefinition,
  RAGCorpus,
  VectorIndexDefinition,
  AIToolDefinition,
  AIAgentDefinition,
  AIEvaluationSuite,
  AIFinOpsMetric,
  AIKillSwitches
} from '../../services/ai/aiPlatformStore';

interface ChiefAIOfficerCommandCenterProps {
  onNavigateToView: (view: AppView) => void;
}

export const ChiefAIOfficerCommandCenter: React.FC<ChiefAIOfficerCommandCenterProps> = ({
  onNavigateToView
}) => {
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'providers-models'
    | 'gateway-routing'
    | 'prompts'
    | 'rag-vector'
    | 'agents-tools'
    | 'guardrails-schemas'
    | 'evals-releases'
    | 'finops-cost'
    | 'kill-switches'
  >('overview');

  const [selectedTaskFilter, setSelectedTaskFilter] = useState<string>('');
  const [killSwitches, setKillSwitches] = useState<AIKillSwitches>(aiPlatformStore.getKillSwitches());

  const providers = aiPlatformStore.getProviders();
  const models = aiPlatformStore.getModels();
  const tasks = aiPlatformStore.getTasks();
  const routes = aiPlatformStore.getRoutes();
  const prompts = aiPlatformStore.getPrompts();
  const corpora = aiPlatformStore.getCorpora();
  const vectorIndexes = aiPlatformStore.getVectorIndexes();
  const tools = aiPlatformStore.getTools();
  const agents = aiPlatformStore.getAgents();
  const evals = aiPlatformStore.getEvals();
  const finops = aiPlatformStore.getFinOps();

  const handleToggleKillSwitch = (key: keyof Omit<AIKillSwitches, 'activeKillSwitchCount'>) => {
    aiPlatformStore.toggleKillSwitch(key);
    setKillSwitches({ ...aiPlatformStore.getKillSwitches() });
  };

  // Aggregated Stats
  const activeModelsCount = models.filter((m) => m.status === 'PRODUCTION').length;
  const activeAgentsCount = agents.filter((a) => a.status === 'PRODUCTION').length;
  const totalDailyRequests = finops.reduce((acc, f) => acc + f.dailyRequests, 0);
  const totalDailyCostUSD = finops.reduce((acc, f) => acc + f.dailyCostUSD, 0);
  const avgPassRatePct = evals.length > 0 ? (evals.reduce((a, e) => a + e.lastRunPassRatePct, 0) / evals.length).toFixed(1) : '99.0';

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#1E293B] font-sans pb-16">
      {/* Top Banner & Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-20 shadow-2xs">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-purple-600 text-white flex items-center justify-center shadow-sm">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold tracking-wider text-purple-700 uppercase bg-purple-50 px-2.5 py-0.5 rounded-full border border-purple-200">
                  AI Control Plane & LLMOps
                </span>
                <span className="text-xs text-[#64748B] font-mono">P28 Operating System</span>
              </div>
              <h1 className="text-xl font-black text-[#0F172A] tracking-tight">
                Chief AI Officer (CAIO) Command Center
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('ai-sandbox-playground')}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-bold flex items-center gap-2 transition-colors cursor-pointer"
            >
              <Play className="w-4 h-4 fill-white" />
              AI Sandbox & Playground
            </button>
            <button
              onClick={() => onNavigateToView('cdo-command-center')}
              className="px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors cursor-pointer"
            >
              <Database className="w-4 h-4" />
              CDO Data OS
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="max-w-7xl mx-auto px-6 overflow-x-auto flex gap-1 border-t border-[#F1F5F9]">
          {[
            { id: 'overview', label: 'Vue Exécutive', icon: PieChart },
            { id: 'providers-models', label: 'Fournisseurs & Modèles', icon: Cpu },
            { id: 'gateway-routing', label: 'AI Gateway & Routage', icon: GitBranch },
            { id: 'prompts', label: 'Prompt Registry', icon: FileCode2 },
            { id: 'rag-vector', label: 'RAGOps & Vector Infra', icon: Search },
            { id: 'agents-tools', label: 'Agents Bornés & Outillage', icon: Zap },
            { id: 'guardrails-schemas', label: 'Guardrails & Schemas', icon: ShieldCheck },
            { id: 'evals-releases', label: 'Évaluations & Releases', icon: Award },
            { id: 'finops-cost', label: 'AI FinOps & Unit Costs', icon: DollarSign },
            { id: 'kill-switches', label: 'Kill Switches & Santé', icon: Power }
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'border-purple-600 text-purple-700 bg-purple-50/50'
                    : 'border-transparent text-[#64748B] hover:text-[#0F172A] hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* EXECUTIVE OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Requetes IA / Jour</div>
                <div className="text-2xl font-black text-[#0F172A] mt-1">{(totalDailyRequests / 1000).toFixed(1)}k</div>
                <div className="text-xs text-purple-600 font-semibold mt-1">100% via AI Gateway</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Modèles en Prod</div>
                <div className="text-2xl font-black text-emerald-600 mt-1">{activeModelsCount}</div>
                <div className="text-xs text-emerald-700 font-semibold mt-1">{models.length} enregistrés</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Agents Bornés</div>
                <div className="text-2xl font-black text-blue-600 mt-1">{activeAgentsCount}</div>
                <div className="text-xs text-blue-700 font-semibold mt-1">Max 5 steps & 2 tools</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Score Evals Moy.</div>
                <div className="text-2xl font-black text-amber-600 mt-1">{avgPassRatePct}%</div>
                <div className="text-xs text-amber-700 font-semibold mt-1">Groundedness & Accuracy</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Coût IA / Jour</div>
                <div className="text-2xl font-black text-indigo-600 mt-1">${totalDailyCostUSD.toFixed(2)}</div>
                <div className="text-xs text-indigo-700 font-semibold mt-1">41.4% cache savings</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Kill Switches</div>
                <div className="text-2xl font-black text-rose-600 mt-1">{killSwitches.activeKillSwitchCount}</div>
                <div className="text-xs text-rose-700 font-semibold mt-1">
                  {killSwitches.activeKillSwitchCount === 0 ? 'Système 100% Actif' : 'Restriction Active'}
                </div>
              </div>
            </div>

            {/* Strategic Pillars */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-purple-600" />
                    Principes Directeurs AI Control Plane
                  </h2>
                  <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold rounded-full">
                    Gouvernance Industrielle
                  </span>
                </div>

                <div className="space-y-3 text-sm text-[#475569]">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">1. NO DIRECT PROVIDER SDK IN BUSINESS CODE</strong>
                    Toute fonctionnalité IA passe impérativement par l&apos;AI Gateway et le registre de tâches.
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">2. NEWER MODEL ≠ BETTER MODEL FOR YAMOOPAY</strong>
                    Un modèle ne passe en production qu&apos;après validation par la suite d&apos;évaluations financières YamooPay.
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">3. DETERMINISTIC CALCULATORS ARE AUTHORITATIVE</strong>
                    L&apos;IA ne recalcule jamais de formule financière de manière générative ; elle invoque les outils déterministes.
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-indigo-600" />
                    Fournisseurs & Répartition de Charge
                  </h2>
                </div>

                <div className="space-y-4">
                  {providers.map((provider) => (
                    <div key={provider.id} className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
                      <div>
                        <div className="font-bold text-[#0F172A] text-sm">{provider.name}</div>
                        <div className="text-xs text-[#64748B]">
                          P95: {provider.p95LatencyMs}ms • Erreurs: {provider.errorRatePct}%
                        </div>
                      </div>
                      <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold">
                        {provider.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PROVIDERS & MODELS */}
        {activeTab === 'providers-models' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-[#0F172A]">Registre des Modèles & Fournisseurs (Model Registry)</h2>
                <p className="text-sm text-[#64748B]">
                  Liste officielle des modèles IA qualifiés avec tarification, fenêtres de contexte et scores d&apos;exactitude.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xs overflow-hidden">
              <div className="p-4 border-b border-[#E2E8F0] font-bold text-slate-800 text-sm flex justify-between items-center">
                <span>Modèles Homologués ({models.length})</span>
                <span className="text-xs text-slate-500 font-mono">Provider Agnostic Internal Keys</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase border-b border-slate-200">
                    <tr>
                      <th className="p-4">Model & Internal Key</th>
                      <th className="p-4">Provider</th>
                      <th className="p-4">Context Window</th>
                      <th className="p-4">Pricing (1k tokens)</th>
                      <th className="p-4">Score Exactitude</th>
                      <th className="p-4">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {models.map((mod) => (
                      <tr key={mod.id} className="hover:bg-slate-50 transition-colors">
                        <td className="p-4">
                          <div className="font-bold text-slate-900">{mod.displayName}</div>
                          <div className="text-xs font-mono text-purple-700 mt-0.5">{mod.internalKey}</div>
                        </td>
                        <td className="p-4 font-mono text-xs text-slate-600">{mod.provider}</td>
                        <td className="p-4 font-mono text-xs">{mod.contextWindowTokens.toLocaleString()} tokens</td>
                        <td className="p-4 font-mono text-xs">
                          In: ${mod.inputCostPer1kTokensUSD} / Out: ${mod.outputCostPer1kTokensUSD}
                        </td>
                        <td className="p-4">
                          <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 font-bold text-xs rounded-full border border-emerald-200">
                            {mod.financialAccuracyScore}%
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            mod.status === 'PRODUCTION' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-slate-100 text-slate-700'
                          }`}>
                            {mod.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* MODEL GATEWAY & ROUTING */}
        {activeTab === 'gateway-routing' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Routage Dynamique & Gateway (AI Model Router)</h2>
              <p className="text-sm text-slate-600 mb-6">
                Redirection automatique des requêtes selon le niveau de risque, les cibles de latence et les politiques de fallback.
              </p>

              <div className="space-y-4">
                {routes.map((r) => (
                  <div key={r.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 flex flex-col md:flex-row justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold bg-indigo-50 text-indigo-700 px-2.5 py-0.5 rounded border border-indigo-200">
                          Task: {r.taskKey}
                        </span>
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
                          {r.status}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-slate-800">
                        Modèle Principal: <strong className="text-purple-700 font-mono">{r.primaryModelId}</strong> ({r.trafficPercentage}% du trafic)
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Fallback Modèles: {r.fallbackModelIds.join(', ')} • Timeout: {r.timeoutMs}ms
                      </div>
                    </div>

                    <div className="text-right flex flex-col justify-between">
                      <div className="text-xs font-bold text-slate-700 bg-white px-3 py-1 rounded-lg border border-slate-200 inline-block">
                        Max Cost: ${r.maxCostUSD}/req
                      </div>
                      <div className="text-xs text-slate-500 font-mono">Reason: {r.reasonCode}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* PROMPT REGISTRY */}
        {activeTab === 'prompts' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Registre des Prompts Versionnés (Prompt Registry)</h2>
              <p className="text-sm text-slate-600 mb-6">
                Chaque prompt système est un asset versionné, immuable et associé à une suite de tests d&apos;évaluations.
              </p>

              <div className="space-y-6">
                {prompts.map((p) => (
                  <div key={p.id} className="p-5 border border-slate-200 rounded-xl bg-slate-50/40">
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <h3 className="text-base font-bold text-slate-900">{p.name}</h3>
                        <div className="text-xs font-mono text-purple-700">Task: {p.taskKey} • Version Active: {p.currentProductionVersion}</div>
                      </div>
                      <span className="px-2.5 py-0.5 bg-purple-50 text-purple-700 font-bold text-xs rounded-full border border-purple-200">
                        Niveau Risque: {p.riskLevel}
                      </span>
                    </div>

                    <div className="space-y-3">
                      {p.versions.map((v, i) => (
                        <div key={i} className="p-3 bg-white border border-slate-200 rounded-lg text-xs">
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-mono font-bold text-slate-900">{v.version}</span>
                            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded font-bold">{v.status}</span>
                          </div>
                          <p className="font-mono text-slate-700 bg-slate-50 p-2 rounded border border-slate-100 mb-2">{v.templateText}</p>
                          <div className="text-slate-500 flex justify-between">
                            <span>Approuvé par: <strong>{v.approvedBy}</strong> ({v.approvedAt})</span>
                            <span>Score Eval: <strong className="text-emerald-600">{v.evalPassRatePct}%</strong></span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* RAG & VECTOR INFRASTRUCTURE */}
        {activeTab === 'rag-vector' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <h2 className="text-lg font-bold text-slate-900 mb-1">Corpus RAG Homologués</h2>
                <p className="text-sm text-slate-600 mb-4">Corpus documentaire certifié pour injection RAG.</p>

                {corpora.map((c) => (
                  <div key={c.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-2 text-xs">
                    <div className="font-bold text-slate-900 text-sm">{c.name}</div>
                    <p className="text-slate-600">{c.description}</p>
                    <div className="text-slate-500 pt-2 border-t border-slate-200 flex justify-between">
                      <span>{c.documentCount} Documents • {c.chunkCount.toLocaleString()} Chunks</span>
                      <span className="text-emerald-600 font-bold">{c.freshnessStatus}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <h2 className="text-lg font-bold text-slate-900 mb-1">Index Vectoriels pgvector</h2>
                <p className="text-sm text-slate-600 mb-4">Stockage et recherche vectorielle native PostgreSQL.</p>

                {vectorIndexes.map((v) => (
                  <div key={v.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-2 text-xs">
                    <div className="font-mono font-bold text-indigo-700 text-sm">{v.name}</div>
                    <div className="text-slate-600">Backend: {v.backend} • Dimension: {v.dimension}d ({v.distanceMetric})</div>
                    <div className="text-slate-500 pt-2 border-t border-slate-200 flex justify-between">
                      <span>Vectors: {v.vectorCount.toLocaleString()}</span>
                      <span className="text-emerald-600 font-bold">{v.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* BOUNDED AGENTS & TOOLS */}
        {activeTab === 'agents-tools' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <h2 className="text-lg font-bold text-slate-900 mb-1">Agents Bornés (Agent Runtime)</h2>
                <p className="text-sm text-slate-600 mb-4">Agents autonomes contrôlés avec limites strictes d&apos;étapes et de budget.</p>

                {agents.map((a) => (
                  <div key={a.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-2 text-xs">
                    <div className="font-bold text-slate-900 text-sm">{a.name}</div>
                    <p className="text-slate-600">{a.purpose}</p>
                    <div className="p-2 bg-white rounded border border-slate-200 space-y-1">
                      <div>Max Steps: <strong>{a.maxSteps}</strong> • Max Tools: <strong>{a.maxToolCalls}</strong></div>
                      <div>Max Cost: <strong>${a.maxCostUSD}</strong> • Timeout: <strong>{a.timeoutSeconds}s</strong></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <h2 className="text-lg font-bold text-slate-900 mb-1">Outillage Déterministe (Tool Registry)</h2>
                <p className="text-sm text-slate-600 mb-4">Fonctions exécutables autorisées côté serveur avec validation stricte.</p>

                {tools.map((t) => (
                  <div key={t.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-2 text-xs">
                    <div className="font-mono font-bold text-purple-700 text-sm">{t.name}</div>
                    <p className="text-slate-600">{t.description}</p>
                    <div className="text-slate-500 pt-2 border-t border-slate-200 flex justify-between">
                      <span>Side Effect: <strong>{t.sideEffect}</strong></span>
                      <span className="text-emerald-600 font-bold">{t.isDeterministicCalculator ? 'Déterministe' : 'Non'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* AI FINOPS */}
        {activeTab === 'finops-cost' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Métrique d&apos;Unité Économique AI FinOps</h2>
              <p className="text-sm text-slate-600 mb-6">Suivi précis du coût par requête, par tâche et économies réalisées via context caching.</p>

              <div className="space-y-4">
                {finops.map((f, i) => (
                  <div key={i} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 flex flex-col md:flex-row justify-between gap-4 text-xs">
                    <div>
                      <div className="font-bold font-mono text-slate-900 text-sm">{f.taskKey}</div>
                      <div className="text-slate-500 mt-1">
                        Requetes quotidiennes: <strong>{f.dailyRequests.toLocaleString()}</strong> • Coût quotidien: <strong>${f.dailyCostUSD.toFixed(2)}</strong>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-sm font-black text-indigo-600">${f.avgCostPerRequestUSD} / req</div>
                      <div className="text-emerald-600 font-bold mt-1">{f.cacheHitRatePct}% Cache Hit Rate</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* KILL SWITCHES & HEALTH */}
        {activeTab === 'kill-switches' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-2">Interrupteurs d&apos;Urgence (AI Kill Switches)</h2>
              <p className="text-sm text-slate-600 mb-6">
                Permet d&apos;interrompre instantanément tout ou partie des fonctionnalités IA sans affecter les calculs financiers déterministes.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'globalAIEnabled', label: 'Interrupteur Général IA (Global AI Kill Switch)', desc: 'Desactive toute fonctionnalité IA sur YamooPay' },
                  { key: 'financialAssistantEnabled', label: 'Assistant Financier & Copilot', desc: 'Desactive l\'assistant de recherche RAG' },
                  { key: 'searchRagEnabled', label: 'Indexation & Recherche Vectorielle RAG', desc: 'Desactive l\'injection documentaire RAG' },
                  { key: 'agenticToolsEnabled', label: 'Exécution d\'Outillage & Agents', desc: 'Desactive les appels de fonctions autonomes' }
                ].map((item) => {
                  const isEnabled = killSwitches[item.key as keyof AIKillSwitches] as boolean;
                  return (
                    <div key={item.key} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 flex items-center justify-between">
                      <div>
                        <div className="font-bold text-slate-900 text-sm">{item.label}</div>
                        <div className="text-xs text-slate-500">{item.desc}</div>
                      </div>
                      <button
                        onClick={() => handleToggleKillSwitch(item.key as any)}
                        className={`px-4 py-2 rounded-lg font-bold text-xs transition-colors cursor-pointer ${
                          isEnabled ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-rose-600 text-white hover:bg-rose-700'
                        }`}
                      >
                        {isEnabled ? 'ACTIF (ON)' : 'DESACTIVÉ (OFF)'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
